import { posthogRequestHeaders } from '../lib/posthog';

const API = '/api';

async function req(path, options = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...posthogRequestHeaders(),
      ...(options.headers || {}),
    },
    ...options,
  });
  let data = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { detail: text };
    }
  }
  if (!res.ok) {
    const detail = data?.detail;
    let msg = typeof detail === 'string' ? detail : detail?.[0]?.msg || res.statusText;
    if (res.status === 403) {
      msg = msg || 'Not authorized for this release action (admin required).';
    } else if (res.status === 429) {
      const retry = res.headers.get('Retry-After');
      msg = retry
        ? `Too many attempts — retry in ${retry}s.`
        : 'Too many attempts — wait and retry.';
    } else if (res.status === 502 && /invite email failed/i.test(String(msg))) {
      msg = `${msg} You can resend the invite once email delivery is restored.`;
    }
    const err = new Error(msg || 'Request failed');
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

async function uploadReq(path, formData) {
  const res = await fetch(`${API}${path}`, {
    credentials: 'include',
    headers: {
      ...posthogRequestHeaders(),
    },
    method: 'POST',
    body: formData,
  });
  let data = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { detail: text };
    }
  }
  if (!res.ok) {
    const detail = data?.detail;
    const msg = typeof detail === 'string' ? detail : detail?.[0]?.msg || res.statusText;
    const err = new Error(msg || 'Upload failed');
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const crmApi = {
  summary: () => req('/crm/summary'),
  listFirms: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') q.set(k, v);
    });
    const qs = q.toString();
    return req(`/crm/firms${qs ? `?${qs}` : ''}`);
  },
  getFirm: (id) => req(`/crm/firms/${id}`),
  createFirm: (body) =>
    req('/crm/firms', { method: 'POST', body: JSON.stringify(body) }),
  deleteFirm: (id) => req(`/crm/firms/${id}`, { method: 'DELETE' }),
  patchFirm: (id, body) =>
    req(`/crm/firms/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  addContact: (firmId, body) =>
    req(`/crm/firms/${firmId}/contacts`, { method: 'POST', body: JSON.stringify(body) }),
  patchContact: (firmId, contactId, body) =>
    req(`/crm/firms/${firmId}/contacts/${contactId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteContact: (firmId, contactId) =>
    req(`/crm/firms/${firmId}/contacts/${contactId}`, { method: 'DELETE' }),
  addActivity: (id, body) =>
    req(`/crm/firms/${id}/activities`, { method: 'POST', body: JSON.stringify(body) }),
  addKnowledge: (id, body) =>
    req(`/crm/firms/${id}/knowledge`, { method: 'POST', body: JSON.stringify(body) }),
  uploadKnowledge: (id, { file, docType = 'transcript', title = '', contactId = null }) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('doc_type', docType);
    if (title) fd.append('title', title);
    if (contactId != null) fd.append('contact_id', String(contactId));
    return uploadReq(`/crm/firms/${id}/knowledge/upload`, fd);
  },
  listKnowledge: (id) => req(`/crm/firms/${id}/knowledge`),
  searchKnowledge: (params = {}) =>
    req('/crm/knowledge/search', { method: 'POST', body: JSON.stringify(params) }),
  createCase: (body) => req('/crm/cases', { method: 'POST', body: JSON.stringify(body) }),
  getCase: (id) => req(`/crm/cases/${id}`),
  approveCase: (id) => req(`/crm/cases/${id}/approve`, { method: 'POST', body: '{}' }),
  revokeCase: (id) => req(`/crm/cases/${id}/revoke`, { method: 'POST', body: '{}' }),
  attachPack: (id, body) =>
    req(`/crm/firms/${id}/packs`, { method: 'POST', body: JSON.stringify(body) }),
  publishPack: (firmId, packId) =>
    req(`/crm/firms/${firmId}/packs/${packId}/publish`, { method: 'POST', body: '{}' }),
  revokePack: (firmId, packId) =>
    req(`/crm/firms/${firmId}/packs/${packId}/revoke`, { method: 'POST', body: '{}' }),
  outreachDraft: (id, contactId) => {
    const q = contactId ? `?contact_id=${contactId}` : '';
    return req(`/crm/firms/${id}/outreach-draft${q}`);
  },
  sendOutreach: (id, body) =>
    req(`/crm/firms/${id}/send-outreach`, { method: 'POST', body: JSON.stringify(body) }),
  invite: (id, body) =>
    req(`/crm/firms/${id}/invite`, { method: 'POST', body: JSON.stringify(body) }),
  importFirms: (payload) =>
    req('/crm/import', { method: 'POST', body: JSON.stringify(payload) }),
  importFintrxSeed: () => req('/crm/import/fintrx', { method: 'POST', body: '{}' }),
};

export const investorApi = {
  portal: () => req('/investor/portal'),
  pack: (id) => req(`/investor/packs/${id}`),
};
